import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';

interface ProductUploadProps {
  imageUrls: string[];
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
  isExample?: boolean; // Add new prop to identify if the image is from an example
}

const ProductUpload: React.FC<ProductUploadProps> = ({ imageUrls, setImageUrls, isExample = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth(); // Get current user from auth context

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file); // Changed from 'files' to 'imageUrlList'
    });
    formData.append('userId', currentUser?.uid || 'anonymous'); // Changed from 'user_id' to 'userId'
    try {
      const response = await fetch(API_CONFIG.PHOTOSHOOT_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      // Check HTTP status
      if (!response.ok) {
        setUploadStatus('Upload failed. Please try again.');
        setImageUrls([]);
        return;
      }

      const data = await response.json();

      // Check success flag and validate response
      if (data.success) {
        if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
          setImageUrls(data.uploaded.map((item: { url: string }) => item.url));
          setUploadStatus('Upload successful!');
        } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
          setImageUrls(data.urls);
          setUploadStatus('Upload successful!');
        } else if (data.url) {
          setImageUrls([data.url]);
          setUploadStatus('Upload successful!');
        } else {
          setUploadStatus('Upload failed. No files received.');
          setImageUrls([]);
        }
      } else {
        setUploadStatus(data.message || 'Upload failed. Please try again.');
        setImageUrls([]);
      }
    } catch (error) {
      setUploadStatus('Upload failed. Please try again.');
      setImageUrls([]);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  // Show preview if images uploaded
  if (imageUrls.length > 0) {
    return (
      <div className="bg-white rounded-lg p-4 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center relative h-[400px]">
        {/* Cross button to close preview and allow new upload */}
        <button
          className="absolute top-2 right-2 text-text-description hover:text-red-500 text-2xl focus:outline-none"
          onClick={() => {
            setImageUrls([]);
            setUploadStatus(null);
          }}
          aria-label="Close preview"
        >
          &times;
        </button>
        <h3 className="font-medium text-text-heading mb-3">
          {isExample ? 'Example Image' : `Uploaded Image${imageUrls.length > 1 ? 's' : ''}`}
        </h3>
        <div className="flex flex-col items-center justify-center w-full h-full">
          {imageUrls.map((url, idx) => (
            <div key={idx} className="flex justify-center items-center w-full h-full">
              <img
                src={url}
                alt={`${isExample ? 'Example' : 'Uploaded'} preview ${idx + 1}`}
                className="w-4/5 object-contain rounded shadow-md max-h-[300px]"
              />
            </div>
          ))}
        </div>
        {!isExample && uploadStatus && (
          <p className="text-green-600 mt-3 text-sm">Upload successful!</p>
        )}
        {isExample && (
          <p className="text-text-description mt-3 text-xs">This is an example image. Upload your own to customize.</p>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-accent-sand rounded-lg p-6 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center h-[400px]"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="image/*"
      />
      <div className="upload-icon-container mb-4">
        <i className="fas fa-cloud-upload-alt text-secondary-600 text-2xl"></i>
      </div>
      <h3 className="font-medium text-text-heading mb-2">Upload Product Images</h3>
      <p className="text-sm text-text-description mb-4 text-center">Drag & drop product images or click to browse</p>
      {uploading ? (
        <div className="flex items-center justify-center h-12">
          <span className="inline-block w-8 h-8 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></span>
        </div>
      ) : (
        <button
          className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-2 rounded-lg font-medium"
          onClick={handleButtonClick}
          disabled={uploading}
        >
          Select Files
        </button>
      )}
      <p className="text-xs text-text-description mt-3">JPG, PNG up to 10MB</p>
      {uploadStatus && (
        <p className={`mt-2 text-sm ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>{uploadStatus}</p>
      )}
    </div>
  );
};

export default ProductUpload; 